import { useEffect, useState, FC, useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import ReactGA from 'react-ga';
import { getEcommerceItemFromProduct } from '@helpers/ecommerce';
import useCart from '@hooks/useCart';
import { isProductBuy } from 'tsClient';
import getCreator from '@tsClient/getCreator';
import { useRouter } from 'next/router';
import ShoppingCart from '@providers/ShoppingCart';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import { Asset, Creator, NoodleProductTypes, StripeAccountType } from '@typings/graphql-models';
import * as ApiModels from '@typings/api-models';
import * as tsClient from '@tsClient';
import { useIsMobile, useUser } from '@hooks';
import LoadingShimmer from '@components/CheckoutNew/LoadingShimmer';
import { STRIPE_KEY } from '@configuration/client';
import useNoodleApi from '@hooks/useNoodleApi';
import CartPaymentForm from '@components/CartPaymentForm';
import CartPaymentFormStep from '@components/CartPaymentFormStep';
import UserImage from '@components/UserImage';
import Image from '@components/Image';
import { SIZES } from '@styles/media';
import CustomLink from '@components/CustomLink';
import CartCheckoutForm from '@components/CartCheckoutForm';
import { useFeatureFlag } from '@providers/FeatureFlags';
import CartBookingForm from '@components/CartBookingForm';
import CartUserInfoForm from '@components/CartUserInfoForm';
import CartTermsForm from '@components/CartTermsForm';
import CartAdditionalTermsInfo from '@components/CartAdditionalTermsInfo';
import { useIsUserInitialized } from '@providers/Auth';
import classes from './checkoutNew.module.scss';

type ThisCreator = Pick<Creator, 'id' | 'stripe_account_id' | 'logoForBranding' | 'stripeAccountType'> & {
  person?: (Pick<ApiModels.Person, 'id'> & { image: Pick<Asset, 'url'> }) | undefined;
} & Parameters<typeof CartPaymentFormStep>[0]['creator'];

type Props = {
  creatorSlug: string;
  isSaaSProduct?: boolean;
};

const LogoOrName: FC<{ creator: ThisCreator }> = ({ creator }) => (
  <>
    {creator.logoForBranding
      ? (
        <Image image={creator.logoForBranding.url} alt={creator.name || ''} isSquare />
      )
      : (
        <>
          <UserImage name={creator.name} size={32} url={creator.person?.image?.url} color="var(--color-primary)" />
          <p>{creator.name}</p>
        </>
      )}
  </>
);

const getLabelFromFieldName = (fieldName: string): string => {
  const label = fieldName.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, str => str.toUpperCase());
  return label;
};

const CheckoutNew: FC<Props> = ({ creatorSlug, isSaaSProduct }) => {
  const useMultiStepCart = useFeatureFlag('new-cart');
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [creator, setCreator] = useState<ThisCreator | null>(null);
  const [isCreator, setIsCreator] = useState<boolean>(false);
  const [hasCheckedCart, setCartChecked] = useState(false);
  const [sessionTime, setSessionTime] = useState<string | null>(null);
  const [sessionDuration, setSessionDuration] = useState<number | null>(null);
  const [extraTermsData, setExtraTermsData] = useState<Record<string, unknown>>({});
  const [userName, setUserName] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [customTermsId, setCustomTermsId] = useState<string | null>(null);
  const [collectUserInfo, setCollectUserInfo] = useState(false);
  const [user] = useUser();
  const userIsInitialized = useIsUserInitialized();
  const router = useRouter();
  const isMobile = useIsMobile(SIZES.md);
  const { cart } = useCart({ creatorSlug });
  const { data: tier, getData: getTierFn, fetchingState: fetchingTier } = useNoodleApi(tsClient.getTierById);
  const [personId, setPersonId] = useState<string | null>(null);
  const [additionalTermFields, setAdditionalTermFields] = useState<{ key: string; label: string }[]>([]);

  const userId = user?.id;
  const itemWithTerms = cart.find(price => price.product.customTermsTemplate);
  const hasProductTerms = Boolean(itemWithTerms);
  const schedulingPrice = cart.find(price => price.sessionDuration && price.sessionInterval);
  const requiresAdditionalTermFields = additionalTermFields.length > 0;

  const templateFieldsData = useMemo(() => ({
    ...extraTermsData,
    sessionDuration,
    sessionTime,
  }), [extraTermsData, sessionDuration, sessionTime]);

  useEffect(() => {
    if (cart[0]?.tierId) {
      getTierFn({ id: cart[0].tierId });
    }
  }, [getTierFn, cart]);

  useEffect(() => {
    if (user?.id && !user?.isAnonymous) {
      setPersonId(user.id);
    }
  }, [user]);

  useEffect(() => {
    if (user?.id === creator?.person?.id) {
      setIsCreator(true);
    }
  }, [creator, user]);

  useEffect(() => {
    if (itemWithTerms) {
      const customFields = itemWithTerms.product.customTermsTemplate?.customTermsDynamicVariables;
      if (customFields && customFields.length > 0) {
        setAdditionalTermFields(customFields.map((field) => ({ key: field.name, label: getLabelFromFieldName(field.name) })));
      }
    }
  }, [itemWithTerms]);

  useEffect(() => {
    if (!collectUserInfo) {
      setCollectUserInfo(hasProductTerms && userIsInitialized && !userId);
    }
  }, [hasProductTerms, userIsInitialized, userId, collectUserInfo, setCollectUserInfo]);

  useEffect(() => {
    const loadStripeInstance = async (): Promise<void> => {
      if (!creator || !creator.stripe_account_id || !STRIPE_KEY) {
        return;
      }
      const newStripe = creator.stripeAccountType !== StripeAccountType.Connect
        ? await loadStripe(STRIPE_KEY)
        : await loadStripe(STRIPE_KEY, { stripeAccount: creator.stripe_account_id });
      setStripe(newStripe);
    };
    loadStripeInstance();
  }, [creator]);

  useEffect(() => {
    cart.forEach(price => {
      ReactGA.plugin.execute('ec', 'addProduct', getEcommerceItemFromProduct(price.product, price));
    });
    ReactGA.plugin.execute('ec', 'setAction', 'checkout', {
      step: 2,
    });
  }, [cart]);

  useEffect(() => {
    const handleBoughtItems = async (): Promise<void> => {
      if (cart.length === 1) {
        const { status: isBought } = cart[0].product.isTierBasedPermissioningEnabled && cart[0].tierId
          ? await tsClient.isTierBuy({ id: cart[0].tierId })
          : await isProductBuy({ id: cart[0].product.id });

        if (isBought) {
          const productTypes = cart[0].product.productTypes.map(p => p.noodleProductType);
          if (productTypes.includes(NoodleProductTypes.Handbook)) {
            await ShoppingCart.removeProduct({ price: cart[0] });
            await router.push(getUrl(IDENTIFIERS.PRODUCT, { creatorSlug, productSlug: cart[0].product.slug }));
          }
        }
      } else {
        await Promise.all(
          cart.map(async price => {
            const { status: isBought } = price.product.isTierBasedPermissioningEnabled && price.tierId
              ? await tsClient.isTierBuy({ id: price.tierId })
              : await isProductBuy({ id: price.product.id });
            if (isBought) {
              const productTypes = price.product.productTypes.map(p => p.noodleProductType);
              if (productTypes.includes(NoodleProductTypes.Handbook)) {
                await ShoppingCart.removeProduct({ price });
              }
            }
          }),
        );
        if (cart.length === 0) {
          await router.push(getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug }));
        }
      }
      setCartChecked(true);
    };
    if (user?.id && !user.isAnonymous && !isCreator) {
      handleBoughtItems();
    } else {
      setCartChecked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isCreator]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const data = await getCreator({ creatorSlug });
      setCreator(data);
    };
    fetchData();
  }, [creatorSlug]);

  const handleSelectBookingTime = async ({
    sessionDuration: newDuration,
    sessionTime: newTime,
  }: {
    sessionDuration: number;
    sessionTime: string;
  }): Promise<void> => {
    setSessionDuration(newDuration);
    setSessionTime(newTime);
  };

  const handleAddFieldsToTermsData = (newFields: Record<string, unknown>): void => {
    setExtraTermsData((prevData) => {
      const updatedData = { ...prevData, ...newFields };
      return updatedData;
    });
  };

  if (!stripe) {
    return <LoadingShimmer />;
  }

  return (
    <Elements stripe={stripe}>
      {isMobile && creator && (
        <CustomLink to={getUrl(IDENTIFIERS.CREATOR, { creatorSlug })} className={classes.creatorNameMobile}>
          <LogoOrName creator={creator} />
        </CustomLink>
      )}
      <div className={classes.checkoutContent}>
        {creator && hasCheckedCart && !fetchingTier.isFetching
          ? (
            <>
              {!isMobile && (
                <CustomLink to={getUrl(IDENTIFIERS.CREATOR, { creatorSlug })} className={classes.creatorNameDesktop}>
                  <LogoOrName creator={creator} />
                </CustomLink>
              )}
              <CartCheckoutForm>
                {schedulingPrice && (
                  <CartBookingForm
                    productId={schedulingPrice.product.id}
                    onSelect={handleSelectBookingTime}
                    priceId={schedulingPrice.id}
                  />
                )}
                {useMultiStepCart && collectUserInfo && (
                  <CartUserInfoForm
                    creatorSlug={creatorSlug}
                    initialEmail={userEmail}
                    initialName={userName}
                    setPersonId={setPersonId}
                    setUserName={setUserName}
                    setUserEmail={setUserEmail}
                  />
                )}
                {useMultiStepCart && itemWithTerms && requiresAdditionalTermFields && (
                  <CartAdditionalTermsInfo
                    additionalFields={additionalTermFields}
                    onSubmit={handleAddFieldsToTermsData}
                  />
                )}
                {useMultiStepCart && itemWithTerms && personId && (
                  <CartTermsForm
                    personId={personId}
                    priceId={itemWithTerms.id}
                    templateFieldsData={templateFieldsData}
                    setCustomTermsId={setCustomTermsId}
                    termsTemplateId={itemWithTerms.product.customTermsTemplate?.id || ''}
                  />
                )}
                {
                  useMultiStepCart
                    ? (
                      <CartPaymentFormStep
                        creator={creator}
                        creatorSlug={creatorSlug}
                        customTermsId={customTermsId || undefined}
                        isSaaSProduct={isSaaSProduct}
                        tierTitle={tier?.title}
                        sessionDuration={sessionDuration || undefined}
                        sessionTime={sessionTime || undefined}
                        personId={personId}
                        initialEmail={userEmail}
                        initialName={userName}
                      />
                    )
                    : (
                      <CartPaymentForm
                        creator={creator}
                        creatorSlug={creatorSlug}
                        isSaaSProduct={isSaaSProduct}
                        tierTitle={tier?.title}
                        sessionDuration={sessionDuration || undefined}
                        sessionTime={sessionTime || undefined}
                      />
                    )
                }
              </CartCheckoutForm>
            </>
          )
          : (
            <LoadingShimmer />
          )}
      </div>
    </Elements>
  );
};

export default CheckoutNew;
